import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { 
    Plus, Save, X, Truck, Users, UserCircle, Box, Mail, Calculator, 
    Building2, Percent, MoreHorizontal, Wallet, DollarSign, CalendarDays, 
    CreditCard, FileText, StickyNote, Receipt, Layers, 
    ChevronDown, ArrowRightLeft, CheckCircle, AlertTriangle,
    Loader2, Tag, Store, Search, User, Building, UserPlus, Plane, Hotel, Home, 
    Languages, Code, Scissors, IdCard,
    List, File, Coins, Clock, ArrowRight,
    ArrowLeft, Info, CheckCheck, Scale,
    Check, Ban, AlertCircle, Circle, ChartLine, ShoppingCart, Utensils, Coffee,
    Wrench, Lightbulb, Zap, Droplets,
    Snowflake, Cog, Shield, ClipboardList, Boxes,
    Hand, Armchair, Sofa, Shirt, Paintbrush,
    Brush, Trash2, Phone, Wifi, Tv, Monitor,
    Printer, Car, Fuel, Leaf, Sprout, Trees,
    Mountain, Umbrella, Sun, Moon, Cloud, CloudRain,
    Snowman, Wind, Globe, Flag, Heart, Star,
    Gift, Gem, Crown, Medal, Trophy, Music,
    Film, Camera, Video, Book, GraduationCap,
    School, Hospital, Ambulance, HeartPulse, Pill,
    Syringe, Bandage, Tooth, Eye, Ear,
    Apple, Carrot, Wheat, Cheese, Egg,
    Fish, Drumstick, Cookie, Candy, IceCream,
    ShoppingBag, Gavel, FileText as FileTextIcon, PenTool, Pencil,
    Ruler, Weight, Scale as Scale2, Hourglass, StopCircle,
    Calendar, Timer,
    Bell, Megaphone, Microphone, Headphones,
    Drum, Guitar, Piano, Theater, Clapperboard,
    Ticket, Mask, Palette, Pencil as Pencil2,
    Ruler as Ruler2, SwatchBook, Droplet, Flask, Vial,
    Magnet, Atom, Microscope, Dna, FlaskRound,
    Microchip, Memory, Server, Database, CloudUpload,
    CloudDownload, RefreshCw, Undo, Redo, History, Clock4,
    HandCoins, PiggyBank, Handshake,
    HandHeart, Users2, UsersRound, ArrowsUpDown,
    HandPlatter, Coins as CoinsIcon, Wallet as Wallet2,
    ArrowUp, ArrowDown, ArrowUpRight, ArrowDownLeft,
    Clock as ClockIcon, ArrowUpIcon, ArrowDownIcon,
    ArrowUpCircle, ArrowDownCircle,
    Phone as PhoneIcon,
    Sofa as Couch,
    Flame,
    Trees as TreesIcon
} from 'lucide-react';
import "../module_mechanics/Styles/Trans.css";

// ==============================================
// API ENDPOINTS
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
// Version query prevents a proxy/service worker from serving an older API response.
const TRANSACTIONS_API = `${API_BASE}/cosmetics_transactions.php?v=20260815-dynamic-daily-oc-v1`;
const CUSTOMERS_API = `${API_BASE}/mechanics_customers.php`;
const SUPPLIERS_API = `${API_BASE}/mechanics_suppliers.php`;
const EMPLOYEES_API = `${API_BASE}/mechanics_employees.php`;
// Partners API is in solar_module
const PARTNERS_API = 'https://ourzestapp.online/oz_backend/solar_module/solar_partners.php';

// ==============================================
// HELPER FUNCTIONS
// ==============================================
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Provider catalog follows the current SBP regulated-institution and authorized
// EMI lists. "Other" keeps the form future-proof when a provider is added later.
const PAKISTAN_BANKS = [
    'Allied Bank Limited', 'Askari Bank Limited', 'Bank Alfalah Limited',
    'Bank AL Habib Limited', 'BankIslami Pakistan Limited', 'Dubai Islamic Bank Pakistan Limited',
    'Faysal Bank Limited', 'Habib Bank Limited (HBL)', 'Habib Metropolitan Bank Limited',
    'JS Bank Limited', 'MCB Bank Limited', 'MCB Islamic Bank Limited',
    'Meezan Bank Limited', 'National Bank of Pakistan', 'Sindh Bank Limited',
    'Samba Bank Limited', 'Soneri Bank Limited', 'Standard Chartered Bank (Pakistan) Limited',
    'The Bank of Khyber', 'The Bank of Punjab', 'United Bank Limited (UBL)',
    'Al Baraka Bank (Pakistan) Limited', 'Bank of China Limited - Pakistan',
    'Citibank N.A. - Pakistan', 'Deutsche Bank AG - Pakistan',
    'Industrial and Commercial Bank of China - Pakistan', 'First Women Bank Limited',
    'Bank Makramah Limited',
    'Zarai Taraqiati Bank Limited', 'Punjab Provincial Cooperative Bank Limited',
    'Easypaisa Bank Limited', 'Mashreq Bank Pakistan Limited',
    'Raqami Islamic Digital Bank Limited', 'HBL Microfinance Bank Limited',
    'NRSP Microfinance Bank Limited', 'Khushhali Microfinance Bank Limited',
    'LOLC Microfinance Bank Limited', 'U Microfinance Bank Limited',
    'Mobilink Microfinance Bank Limited', 'APNA Microfinance Bank Limited',
    'Halan Microfinance Bank Limited', 'Sindh Microfinance Bank Limited',
    'ABHI Microfinance Bank Limited', 'ASA Microfinance Bank (Pakistan) Limited',
    'Other / Custom Bank'
];

const PAKISTAN_DIGITAL_WALLETS = [
    'Easypaisa', 'JazzCash', 'UPaisa', 'HBL Konnect', 'UBL Omni', 'Zindigi',
    'NayaPay', 'SadaPay', 'OPay', 'Digitt+', 'OneZapp', 'Keenu', 'Hubpay',
    'YAP Pakistan', 'Other / Custom Wallet'
];

const createDailyAccount = (type, overrides = {}) => ({
    id: overrides.id || `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    provider_name: '',
    custom_provider: '',
    account_label: '',
    account_number: '',
    opening: '',
    closing: '',
    ...overrides
});

const normalizeDailyAccounts = (accounts, summary = {}) => {
    if (Array.isArray(accounts) && accounts.length > 0) {
        return accounts.map((account, index) => {
            const type = account.type === 'wallet' ? 'wallet' : 'bank';
            const catalog = type === 'wallet' ? PAKISTAN_DIGITAL_WALLETS : PAKISTAN_BANKS;
            const savedProvider = String(account.provider_name || '').trim();
            const isCatalogProvider = catalog.includes(savedProvider);
            return createDailyAccount(type, {
                ...account,
                id: account.id || `saved-${index}`,
                provider_name: isCatalogProvider ? savedProvider : (type === 'wallet' ? 'Other / Custom Wallet' : 'Other / Custom Bank'),
                custom_provider: isCatalogProvider ? '' : (account.custom_provider || savedProvider),
                opening: Number.parseFloat(account.opening) || 0,
                closing: Number.parseFloat(account.closing) || 0
            });
        });
    }

    const legacy = [];
    if ((Number.parseFloat(summary.bank_opening) || 0) !== 0 || (Number.parseFloat(summary.bank_closing) || 0) !== 0) {
        legacy.push(createDailyAccount('bank', {
            id: 'legacy-bank', provider_name: 'Other / Custom Bank',
            custom_provider: 'Legacy bank total', account_label: 'Legacy bank total',
            opening: Number.parseFloat(summary.bank_opening) || 0,
            closing: Number.parseFloat(summary.bank_closing) || 0
        }));
    }
    if ((Number.parseFloat(summary.wallet_opening) || 0) !== 0 || (Number.parseFloat(summary.wallet_closing) || 0) !== 0) {
        legacy.push(createDailyAccount('wallet', {
            id: 'legacy-wallet', provider_name: 'Other / Custom Wallet',
            custom_provider: 'Legacy wallet total', account_label: 'Legacy wallet total',
            opening: Number.parseFloat(summary.wallet_opening) || 0,
            closing: Number.parseFloat(summary.wallet_closing) || 0
        }));
    }
    return legacy;
};

const getDailyTotals = (dailyData = {}) => {
    const accounts = Array.isArray(dailyData.accounts) ? dailyData.accounts : [];
    const accountsOpening = accounts.reduce((sum, account) => sum + (Number.parseFloat(account.opening) || 0), 0);
    const accountsClosing = accounts.reduce((sum, account) => sum + (Number.parseFloat(account.closing) || 0), 0);
    const opening = (Number.parseFloat(dailyData.cash_opening) || 0) +
        (Number.parseFloat(dailyData.cheque_opening) || 0) + accountsOpening;
    const closing = (Number.parseFloat(dailyData.cash_closing) || 0) +
        (Number.parseFloat(dailyData.cheque_closing) || 0) + accountsClosing;
    return { opening, closing, net: closing - opening };
};

const getSimpleBalanceLabel = (entityType, balance, fallback = 'Balance') => {
    const amount = Number.parseFloat(balance) || 0;
    if (Math.abs(amount) <= 0.005 && (entityType === 'customer' || entityType === 'supplier')) {
        return 'Settled';
    }
    if (entityType === 'customer') {
        return amount < -0.005 ? 'I have to pay customer' : 'Customer will pay me';
    }
    if (entityType === 'supplier') {
        return amount < -0.005 ? 'Supplier will pay me' : 'I have to pay supplier';
    }
    return fallback;
};

// ==============================================
// TRANSLATION SYSTEM
// ==============================================
const translations = {
    'en': {
        addTransaction: 'Add Transaction',
        addNewTransaction: 'Record a new financial transaction',
        // Dropdown Options
        dailyOpeningClosing: 'Daily Opening / Closing',
        customerPayment: 'Customer Payment',
        customerDue: 'Customer Due',
        supplierPayment: 'Supplier Payment',
        supplierDue: 'Supplier Due',
        partnerInvestment: 'Partner Investment',
        partnerPayment: 'Partner Payment',
        employeeSalary: 'Salary Payment',
        shopExpense: 'Shop Expense',
        // Descriptions
        dailyOpeningDesc: 'Manage daily cash, bank & cheque opening/closing balances',
        customerPaymentDesc: 'Incoming payment received from customers',
        customerDueDesc: 'Record an amount the customer will pay me',
        supplierPaymentDesc: 'Outgoing payments made to suppliers',
        supplierDueDesc: 'Record an amount I have to pay the supplier',
        partnerInvestmentDesc: 'Incoming investment from partners',
        partnerPaymentDesc: 'Outgoing payments made to partners',
        employeeSalaryDesc: 'Outgoing salary payments to employees',
        expenseDesc: 'Outgoing expenses for shop operations',
        // Opening/Closing Labels
        cashOpening: 'Cash Opening',
        cashClosing: 'Cash Closing',
        bankOpening: 'Bank Opening',
        bankClosing: 'Bank Closing',
        chequeOpening: 'Cheque Opening',
        chequeClosing: 'Cheque Closing',
        totalIncoming: 'Total Incoming',
        totalOutgoing: 'Total Outgoing',
        netBalance: 'Net Balance',
        // Existing labels
        selectCustomer: 'Select Customer',
        selectSupplier: 'Select Supplier',
        selectEmployee: 'Select Employee',
        selectPartner: 'Select Partner',
        amount: 'Amount',
        date: 'Date',
        description: 'Description',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        creditCard: 'Credit Card',
        mobilePayment: 'Mobile Payment',
        invoiceNumber: 'Invoice Number',
        optional: 'Optional',
        save: 'Save',
        clear: 'Clear',
        saving: 'Saving...',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        transactionSaved: 'Transaction saved successfully!',
        failedSave: 'Failed to save transaction',
        selectShopFirst: 'Please select a shop first',
        noShopSelected: 'No shop selected',
        expenseCategory: 'Expense Category',
        expenseName: 'Expense Name',
        unpaidInvoices: 'Unpaid Invoices',
        noUnpaidInvoices: 'No unpaid invoices found',
        grandTotal: 'Grand Total',
        alreadyPaid: 'Already Paid',
        balanceDue: 'Balance Due',
        paymentType: 'Payment Type',
        fullPayment: 'Full Payment',
        partialPayment: 'Partial Payment',
        paymentDetails: 'Payment Details',
        notes: 'Notes',
        selectReference: 'Select Reference',
        expenseDetails: 'Expense Details',
        general: 'General',
        searchPlaceholder: 'Search...',
        noResults: 'No results found',
        select: 'Select',
        invoice: 'Invoice',
        due: 'Due',
        paid: 'Paid',
        total: 'Total',
        customer: 'Customer',
        supplier: 'Supplier',
        employee: 'Employee',
        partner: 'Partner',
        name: 'Name',
        phone: 'Phone',
        email: 'Email',
        reference: 'Reference',
        selectInvoice: 'Select Invoice',
        paymentAmount: 'Payment Amount',
        paymentEntryType: 'Payment Entry Type',
        regularPayment: 'Regular Payment',
        regularPaymentHint: 'Apply payment against an existing balance or invoice',
        advanceReceivedCustomer: 'Advance Received from Customer',
        advanceReceivedCustomerHint: 'Customer pays before a sale',
        advancePaidSupplier: 'Advance Paid to Supplier',
        advancePaidSupplierHint: 'I pay the supplier before a purchase',
        advanceReturnCustomer: 'Advance Return to Customer',
        advanceReturnCustomerHint: 'I return the customer\'s unused advance',
        advanceReturnSupplier: 'Advance Returned Back by Supplier',
        advanceReturnSupplierHint: 'Supplier returns my unused advance',
        totalBalance: 'Total Balance',
        outstandingBalance: 'Outstanding Balance',
        selectInvoices: 'Select Invoices',
        invoiceSelected: 'Invoice Selected',
        pay: 'Pay',
        balance: 'Balance',
        totalDue: 'Total Due',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        remainingBalance: 'Remaining Balance',
        noSupplierSelected: 'No supplier selected',
        selectSupplierFirst: 'Please select a supplier first',
        paymentAmountExceeds: 'Payment amount exceeds selected invoice total',
        invoicesSelected: 'invoices selected',
        totalSelected: 'Total Selected',
        paymentWillBeApplied: 'Payment will be applied to selected invoices using FIFO (oldest first)',
        supplierBalance: 'Supplier Balance',
        loadingInvoices: 'Loading invoices...',
        fifoNote: 'FIFO: Payments are applied to oldest invoices first',
        totalOutstanding: 'Total Outstanding',
        selectInvoicesToPay: 'Select invoices to pay',
        payment: 'Payment',
        pending: 'Pending',
        partiallyPaid: 'Partially Paid',
        totalPaid: 'Total Paid',
        totalPurchases: 'Total Purchases',
        summary: 'Summary',
        selectCategory: 'Select a category...',
        incoming: 'Incoming',
        outgoing: 'Outgoing',
        opening: 'Opening',
        closing: 'Closing',
        dailySummary: 'Daily Summary',
        transactionDetails: 'Transaction Details',
        customerOwes: 'Customer will pay me',
        youOweSupplier: 'I have to pay',
        customerPays: 'Customer Pays You',
        youPaySupplier: 'You Pay Supplier',
        selectType: 'Select Transaction Type',
        saveDailySummary: 'Save Daily Summary',
        dailySummarySaved: 'Daily Summary saved successfully',
        totalDebit: 'Total Outgoing',
        totalCredit: 'Total Incoming'
    },
};

// ==============================================
// EXPENSE CATEGORIES WITH ICONS
// ==============================================
const EXPENSE_CATEGORIES = [
    // 🏪 Business Operations
    { value: 'rent', label: 'Rent / Lease', icon: Building2, group: 'Business Operations' },
    { value: 'utilities', label: 'Utilities (Electricity, Water, Gas)', icon: Zap, group: 'Business Operations' },
    { value: 'internet', label: 'Internet & Phone', icon: Wifi, group: 'Business Operations' },
    { value: 'maintenance', label: 'Maintenance & Repairs', icon: Wrench, group: 'Business Operations' },
    { value: 'cleaning', label: 'Cleaning & Sanitation', icon: Brush, group: 'Business Operations' },
    { value: 'security', label: 'Security Services', icon: Shield, group: 'Business Operations' },
    { value: 'insurance', label: 'Business Insurance', icon: Shield, group: 'Business Operations' },
    { value: 'licenses', label: 'Licenses & Permits', icon: FileText, group: 'Business Operations' },
    
    // 🍽️ Food & Dining
    { value: 'eating_out', label: 'Eating Out / Restaurants', icon: Utensils, group: 'Food & Dining' },
    { value: 'groceries', label: 'Groceries / Supplies', icon: ShoppingCart, group: 'Food & Dining' },
    { value: 'coffee_break', label: 'Coffee / Tea Break', icon: Coffee, group: 'Food & Dining' },
    { value: 'staff_meals', label: 'Staff Meals / Catering', icon: Users, group: 'Food & Dining' },
    { value: 'serving', label: 'Serving / Hospitality', icon: Hand, group: 'Food & Dining' },
    { value: 'guest_treats', label: 'Guest Treats / Entertainment', icon: Gift, group: 'Food & Dining' },
    { value: 'kitchen_supplies', label: 'Kitchen Supplies', icon: Utensils, group: 'Food & Dining' },
    { value: 'dining_equipment', label: 'Dining Equipment', icon: Sofa, group: 'Food & Dining' },
    
    // 👥 Staff & Employees
    { value: 'salary', label: 'Salary / Wages', icon: DollarSign, group: 'Staff & Employees' },
    { value: 'bonus', label: 'Bonuses & Incentives', icon: Trophy, group: 'Staff & Employees' },
    { value: 'allowances', label: 'Allowances (Travel, Fuel)', icon: Car, group: 'Staff & Employees' },
    { value: 'training', label: 'Training & Development', icon: GraduationCap, group: 'Staff & Employees' },
    { value: 'uniforms', label: 'Uniforms / Workwear', icon: Shirt, group: 'Staff & Employees' },
    { value: 'medical', label: 'Medical / Health Insurance', icon: Hospital, group: 'Staff & Employees' },
    { value: 'recruitment', label: 'Recruitment & Hiring', icon: UserPlus, group: 'Staff & Employees' },
    { value: 'employee_benefits', label: 'Employee Benefits', icon: Heart, group: 'Staff & Employees' },
    
    // 🔧 Repair & Maintenance
    { value: 'repair', label: 'General Repairs', icon: Wrench, group: 'Repair & Maintenance' },
    { value: 'equipment_repair', label: 'Equipment Repair', icon: Cog, group: 'Repair & Maintenance' },
    { value: 'vehicle_repair', label: 'Vehicle Repair', icon: Car, group: 'Repair & Maintenance' },
    { value: 'furniture_repair', label: 'Furniture Repair', icon: Sofa, group: 'Repair & Maintenance' },
    { value: 'building_repair', label: 'Building Repair', icon: Building, group: 'Repair & Maintenance' },
    { value: 'plumbing', label: 'Plumbing Services', icon: Droplets, group: 'Repair & Maintenance' },
    { value: 'electrical', label: 'Electrical Services', icon: Lightbulb, group: 'Repair & Maintenance' },
    { value: 'hvac', label: 'HVAC Services', icon: Snowflake, group: 'Repair & Maintenance' },
    
    // 🛒 Inventory & Stock
    { value: 'inventory', label: 'Inventory Purchase', icon: Boxes, group: 'Inventory & Stock' },
    { value: 'raw_materials', label: 'Raw Materials', icon: Layers, group: 'Inventory & Stock' },
    { value: 'packaging', label: 'Packaging Materials', icon: Box, group: 'Inventory & Stock' },
    { value: 'supplies', label: 'Office Supplies', icon: ClipboardList, group: 'Inventory & Stock' },
    { value: 'stationery', label: 'Stationery', icon: FileText, group: 'Inventory & Stock' },
    { value: 'warehouse', label: 'Warehouse Storage', icon: Store, group: 'Inventory & Stock' },
    { value: 'inventory_management', label: 'Inventory Management', icon: List, group: 'Inventory & Stock' },
    
    // 💡 Utilities & Services
    { value: 'electricity', label: 'Electricity Bills', icon: Lightbulb, group: 'Utilities & Services' },
    { value: 'water', label: 'Water Bills', icon: Droplets, group: 'Utilities & Services' },
    { value: 'gas', label: 'Gas / Fuel', icon: Flame, group: 'Utilities & Services' },
    { value: 'heating', label: 'Heating / AC', icon: Snowflake, group: 'Utilities & Services' },
    { value: 'sewage', label: 'Sewage / Waste', icon: Trash2, group: 'Utilities & Services' },
    { value: 'garbage', label: 'Garbage Disposal', icon: Trash2, group: 'Utilities & Services' },
    { value: 'recycling', label: 'Recycling Services', icon: Leaf, group: 'Utilities & Services' },
    
    // 📱 Communication
    { value: 'phone_bills', label: 'Phone Bills', icon: Phone, group: 'Communication' },
    { value: 'internet_bill', label: 'Internet Bills', icon: Wifi, group: 'Communication' },
    { value: 'postage', label: 'Postage & Courier', icon: Mail, group: 'Communication' },
    { value: 'broadcasting', label: 'Broadcasting', icon: Megaphone, group: 'Communication' },
    { value: 'newsletter', label: 'Newsletter / Publications', icon: FileText, group: 'Communication' },
    
    // 🖥️ Technology
    { value: 'software', label: 'Software Subscriptions', icon: Monitor, group: 'Technology' },
    { value: 'hardware', label: 'Hardware / Equipment', icon: Printer, group: 'Technology' },
    { value: 'website', label: 'Website Hosting', icon: Globe, group: 'Technology' },
    { value: 'it_services', label: 'IT Services', icon: Shield, group: 'Technology' },
    { value: 'cloud_services', label: 'Cloud Services', icon: Cloud, group: 'Technology' },
    { value: 'cybersecurity', label: 'Cybersecurity', icon: Shield, group: 'Technology' },
    { value: 'tech_support', label: 'Tech Support', icon: Cog, group: 'Technology' },
    
    // 🚗 Transportation
    { value: 'fuel', label: 'Fuel / Petrol', icon: Fuel, group: 'Transportation' },
    { value: 'tolls', label: 'Tolls & Parking', icon: DollarSign, group: 'Transportation' },
    { value: 'public_transport', label: 'Public Transport', icon: Truck, group: 'Transportation' },
    { value: 'delivery', label: 'Delivery Services', icon: Truck, group: 'Transportation' },
    { value: 'vehicle_maintenance', label: 'Vehicle Maintenance', icon: Car, group: 'Transportation' },
    { value: 'car_rental', label: 'Car Rental', icon: Car, group: 'Transportation' },
    { value: 'flights', label: 'Flights / Air Travel', icon: Plane, group: 'Transportation' },
    { value: 'hotels', label: 'Hotels / Accommodation', icon: Hotel, group: 'Transportation' },
    
    // 🏢 Property
    { value: 'property_tax', label: 'Property Tax', icon: Building, group: 'Property' },
    { value: 'property_insurance', label: 'Property Insurance', icon: Shield, group: 'Property' },
    { value: 'landscaping', label: 'Landscaping', icon: Trees, group: 'Property' },
    { value: 'pest_control', label: 'Pest Control', icon: Ban, group: 'Property' },
    { value: 'mortgage', label: 'Mortgage Payments', icon: Home, group: 'Property' },
    { value: 'property_management', label: 'Property Management', icon: Building, group: 'Property' },
    
    // 🎉 Events & Entertainment
    { value: 'events', label: 'Events / Functions', icon: Star, group: 'Events & Entertainment' },
    { value: 'entertainment', label: 'Entertainment', icon: Film, group: 'Events & Entertainment' },
    { value: 'decorations', label: 'Decorations', icon: Paintbrush, group: 'Events & Entertainment' },
    { value: 'photography', label: 'Photography', icon: Camera, group: 'Events & Entertainment' },
    { value: 'music', label: 'Music / DJ', icon: Music, group: 'Events & Entertainment' },
    { value: 'catering', label: 'Catering Services', icon: Utensils, group: 'Events & Entertainment' },
    { value: 'venue_rental', label: 'Venue Rental', icon: Building, group: 'Events & Entertainment' },
    { value: 'party_supplies', label: 'Party Supplies', icon: Gift, group: 'Events & Entertainment' },
    
    // 📋 Professional Services
    { value: 'legal', label: 'Legal Fees', icon: Scale, group: 'Professional Services' },
    { value: 'accounting', label: 'Accounting / Audit', icon: Calculator, group: 'Professional Services' },
    { value: 'consulting', label: 'Consulting Services', icon: UserCircle, group: 'Professional Services' },
    { value: 'marketing', label: 'Marketing & Advertising', icon: ChartLine, group: 'Professional Services' },
    { value: 'pr_services', label: 'PR Services', icon: Megaphone, group: 'Professional Services' },
    { value: 'translation', label: 'Translation Services', icon: Languages, group: 'Professional Services' },
    { value: 'graphic_design', label: 'Graphic Design', icon: Palette, group: 'Professional Services' },
    { value: 'web_development', label: 'Web Development', icon: Code, group: 'Professional Services' },
    
    // 🏥 Health & Safety
    { value: 'safety', label: 'Safety Equipment', icon: Shield, group: 'Health & Safety' },
    { value: 'first_aid', label: 'First Aid Supplies', icon: Bandage, group: 'Health & Safety' },
    { value: 'sanitation', label: 'Sanitation Products', icon: Hand, group: 'Health & Safety' },
    { value: 'ppe', label: 'PPE Equipment', icon: Shield, group: 'Health & Safety' },
    { value: 'medical_supplies', label: 'Medical Supplies', icon: Pill, group: 'Health & Safety' },
    { value: 'health_screenings', label: 'Health Screenings', icon: HeartPulse, group: 'Health & Safety' },
    { value: 'wellness_programs', label: 'Wellness Programs', icon: Leaf, group: 'Health & Safety' },
    
    // 🌿 Environment
    { value: 'recycling_service', label: 'Recycling / Waste', icon: Leaf, group: 'Environment' },
    { value: 'green_energy', label: 'Green Energy', icon: Sprout, group: 'Environment' },
    { value: 'environmental', label: 'Environmental Fees', icon: Trees, group: 'Environment' },
    { value: 'sustainability', label: 'Sustainability Programs', icon: Globe, group: 'Environment' },
    { value: 'carbon_offset', label: 'Carbon Offset', icon: Cloud, group: 'Environment' },
    
    // 🎓 Education
    { value: 'education', label: 'Education / Courses', icon: GraduationCap, group: 'Education' },
    { value: 'books', label: 'Books & Materials', icon: Book, group: 'Education' },
    { value: 'workshops', label: 'Workshops / Seminars', icon: School, group: 'Education' },
    { value: 'tuition', label: 'Tuition Fees', icon: Building2, group: 'Education' },
    { value: 'certifications', label: 'Certifications', icon: Medal, group: 'Education' },
    
    // 💰 Financial
    { value: 'bank_fees', label: 'Bank Fees', icon: Building2, group: 'Financial' },
    { value: 'interest', label: 'Interest Payments', icon: Percent, group: 'Financial' },
    { value: 'tax', label: 'Tax Payments', icon: Scale, group: 'Financial' },
    { value: 'investment', label: 'Investments', icon: ChartLine, group: 'Financial' },
    { value: 'loan_payment', label: 'Loan Payments', icon: DollarSign, group: 'Financial' },
    { value: 'dividends', label: 'Dividends', icon: Coins, group: 'Financial' },
    { value: 'currency_exchange', label: 'Currency Exchange', icon: ArrowRightLeft, group: 'Financial' },
    
    // 🎨 Creative & Design
    { value: 'art_supplies', label: 'Art Supplies', icon: Paintbrush, group: 'Creative & Design' },
    { value: 'design_software', label: 'Design Software', icon: PenTool, group: 'Creative & Design' },
    { value: 'printing', label: 'Printing Services', icon: Printer, group: 'Creative & Design' },
    { value: 'fabric', label: 'Fabric / Materials', icon: Shirt, group: 'Creative & Design' },
    { value: 'craft_supplies', label: 'Craft Supplies', icon: Scissors, group: 'Creative & Design' },
    
    // 🏠 General
    { value: 'general', label: 'General / Other', icon: List, group: 'General' },
    { value: 'miscellaneous', label: 'Miscellaneous', icon: MoreHorizontal, group: 'General' },
    { value: 'petty_cash', label: 'Petty Cash', icon: Coins, group: 'General' },
    { value: 'donations', label: 'Donations / Charitable', icon: Heart, group: 'General' },
    { value: 'membership', label: 'Membership Fees', icon: IdCard, group: 'General' },
    { value: 'subscription', label: 'Subscriptions', icon: CalendarDays, group: 'General' }
];

// ==============================================
// CUSTOM EXPENSE CATEGORY DROPDOWN COMPONENT
// ==============================================
const CustomExpenseDropdown = ({
    value,
    onChange,
    disabled,
    placeholder,
    t,
    isRTL
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);

    // Get unique groups for grouping
    const groups = [...new Set(EXPENSE_CATEGORIES.map(cat => cat.group))];

    // Filter categories based on search
    const filteredCategories = EXPENSE_CATEGORIES.filter(cat =>
        cat.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cat.group.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cat.value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Find selected category
    const selectedCategory = EXPENSE_CATEGORIES.find(cat => cat.value === value);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle selection - UPDATED to always update expense name
    const handleSelect = (category) => {
        onChange(category.value);
        // The parent component will handle updating expense_name
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Toggle dropdown
    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setSearchTerm('');
                setHighlightedIndex(0);
            }
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredCategories.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredCategories.length) {
                    handleSelect(filteredCategories[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setSearchTerm('');
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                setSearchTerm('');
                break;
            default:
                break;
        }
    };

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.costrans-custom-dropdown-item');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    return (
        <div 
            className="costrans-custom-dropdown-container" 
            ref={dropdownRef}
            style={{ position: 'relative', width: '100%' }}
        >
            {/* Dropdown Trigger */}
            <div 
                className={`costrans-custom-dropdown-trigger ${isOpen ? 'costrans-open' : ''} ${disabled ? 'costrans-disabled' : ''}`}
                onClick={toggleDropdown}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '0.7rem 32px 0.7rem 0.7rem',
                    border: `1px solid var(--costrans-border)`,
                    borderRadius: '0',
                    background: disabled ? 'var(--costrans-surface)' : 'var(--costrans-bg)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    minHeight: '42px',
                    position: 'relative',
                    transition: 'var(--costrans-transition)',
                    opacity: disabled ? 0.6 : 1,
                    boxSizing: 'border-box',
                    outline: 'none'
                }}
            >
                {selectedCategory ? (
                    <>
                        {React.createElement(selectedCategory.icon, { 
                            size: 18, 
                            style: { 
                                color: 'var(--costrans-primary)', 
                                flexShrink: 0,
                                width: '20px'
                            } 
                        })}
                        <span style={{ 
                            flex: 1, 
                            fontSize: '0.85rem', 
                            color: 'var(--costrans-text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {selectedCategory.label}
                        </span>
                    </>
                ) : (
                    <span style={{ 
                        color: 'var(--costrans-text-light)', 
                        fontSize: '0.85rem',
                        opacity: 0.7
                    }}>
                        {placeholder || t('selectCategory') || 'Select a category...'}
                    </span>
                )}
                <ChevronDown 
                    size={14}
                    style={{
                        position: 'absolute',
                        right: isRTL ? 'auto' : '0.7rem',
                        left: isRTL ? '0.7rem' : 'auto',
                        color: 'var(--costrans-text-light)',
                        transition: 'transform 0.3s ease',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }} 
                />
            </div>

            {/* Dropdown Options */}
            {isOpen && (
                <div 
                    className="costrans-custom-dropdown-options"
                    ref={menuRef}
                    role="listbox"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: 'var(--costrans-bg)',
                        border: `1px solid var(--costrans-border)`,
                        borderRadius: '0',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                        maxHeight: '360px',
                        overflowY: 'auto',
                        zIndex: 9999,
                        padding: '8px 0',
                        animation: 'costransDropdownSlideIn 0.25s ease'
                    }}
                >
                    {/* Search Input */}
                    <div style={{ padding: '0 12px 8px 12px', position: 'sticky', top: 0, background: 'var(--costrans-bg)', zIndex: 2 }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            border: `1px solid var(--costrans-border-light)`,
                            borderRadius: '0',
                            background: 'var(--costrans-surface)',
                            transition: 'var(--costrans-transition)'
                        }}>
                            <Search size={14} style={{ color: 'var(--costrans-text-light)' }} />
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setHighlightedIndex(0);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < filteredCategories.length) {
                                        e.preventDefault();
                                        handleSelect(filteredCategories[highlightedIndex]);
                                    }
                                }}
                                placeholder="Search categories..."
                                style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    color: 'var(--costrans-text)',
                                    fontSize: '0.85rem',
                                    flex: 1,
                                    padding: '4px 0',
                                    fontFamily: 'inherit'
                                }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSearchTerm('');
                                        setHighlightedIndex(0);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--costrans-text-light)',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        padding: '2px 6px'
                                    }}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Categories List */}
                    {filteredCategories.length === 0 ? (
                        <div style={{
                            padding: '30px 20px',
                            textAlign: 'center',
                            color: 'var(--costrans-text-light)',
                            fontSize: '0.85rem'
                        }}>
                            <Search size={24} style={{ opacity: 0.3, marginBottom: '8px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
                            No categories found
                        </div>
                    ) : (
                        <div>
                            {groups.map(group => {
                                const groupCategories = filteredCategories.filter(cat => cat.group === group);
                                if (groupCategories.length === 0) return null;
                                return (
                                    <div key={group}>
                                        {/* Group Header */}
                                        <div style={{
                                            padding: '6px 16px',
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            color: 'var(--costrans-text-light)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.8px',
                                            borderTop: `1px solid var(--costrans-border-light)`,
                                            marginTop: group !== groups[0] ? '4px' : 0,
                                            paddingTop: group !== groups[0] ? '8px' : '6px',
                                            background: 'var(--costrans-surface)',
                                            position: 'sticky',
                                            top: '52px',
                                            zIndex: 1
                                        }}>
                                            {group}
                                        </div>
                                        {groupCategories.map((category, index) => {
                                            const actualIndex = filteredCategories.indexOf(category);
                                            return (
                                                <div
                                                    key={category.value}
                                                    className={`costrans-custom-dropdown-item ${value === category.value ? 'costrans-selected' : ''} ${highlightedIndex === actualIndex ? 'costrans-highlighted' : ''}`}
                                                    onClick={() => handleSelect(category)}
                                                    onMouseEnter={() => setHighlightedIndex(actualIndex)}
                                                    role="option"
                                                    aria-selected={value === category.value}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        padding: '8px 16px',
                                                        cursor: 'pointer',
                                                        transition: 'var(--costrans-transition)',
                                                        background: value === category.value ? 'rgba(179,185,16,0.08)' : (highlightedIndex === actualIndex ? 'var(--costrans-surface)' : 'transparent'),
                                                        borderLeft: value === category.value ? `3px solid var(--costrans-primary)` : (highlightedIndex === actualIndex ? '3px solid var(--costrans-border)' : '3px solid transparent'),
                                                        borderRadius: '0'
                                                    }}
                                                >
                                                    {React.createElement(category.icon, { 
                                                        size: 16, 
                                                        style: { 
                                                            color: value === category.value ? 'var(--costrans-primary)' : 'var(--costrans-text-light)',
                                                            flexShrink: 0,
                                                            width: '20px'
                                                        } 
                                                    })}
                                                    <span style={{
                                                        flex: 1,
                                                        fontSize: '0.85rem',
                                                        color: value === category.value ? 'var(--costrans-text)' : 'var(--costrans-text)'
                                                    }}>
                                                        {category.label}
                                                    </span>
                                                    {value === category.value && (
                                                        <Check 
                                                            size={14} 
                                                            style={{ 
                                                                color: 'var(--costrans-primary)',
                                                                flexShrink: 0
                                                            }} 
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <style>{`
                @keyframes costransDropdownSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-8px) scale(0.98);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                
                .costrans-custom-dropdown-options::-webkit-scrollbar {
                    width: 4px;
                }
                .costrans-custom-dropdown-options::-webkit-scrollbar-track {
                    background: var(--costrans-surface);
                }
                .costrans-custom-dropdown-options::-webkit-scrollbar-thumb {
                    background: var(--costrans-border);
                }
                .costrans-custom-dropdown-options::-webkit-scrollbar-thumb:hover {
                    background: var(--costrans-primary);
                }
                
                .costrans-dark .costrans-custom-dropdown-options {
                    background: var(--costrans-bg);
                    border-color: var(--costrans-border);
                    box-shadow: 0 10px 25px rgba(0,0,0,0.4);
                }
                
                .costrans-dark .costrans-custom-dropdown-trigger {
                    border-color: var(--costrans-border);
                }
                
                .costrans-dark .costrans-custom-dropdown-item {
                    color: var(--costrans-text);
                }
                
                .costrans-dark .costrans-custom-dropdown-item:hover {
                    background: var(--costrans-surface) !important;
                }
                
                .costrans-dark .costrans-custom-dropdown-item.costrans-selected {
                    background: rgba(179,185,16,0.15) !important;
                }
                
                .costrans-custom-dropdown-trigger:focus {
                    border-color: var(--costrans-primary);
                    box-shadow: 0 0 0 2px rgba(179,185,16,0.2);
                }
            `}</style>
        </div>
    );
};

// ==============================================
// TOAST NOTIFICATION
// ==============================================
const ToastNotification = ({ toast, setToast, t }) => {
    if (!toast.show) return null;
    return (
        <div className={`costrans_toast-notification ${toast.type}`}>
            <div className="costrans_toast-content">
                <div className="costrans_toast-icon">
                    {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                </div>
                <div className="costrans_toast-text">
                    <div className="costrans_toast-title">{t(toast.type === 'success' ? 'success' : 'error')}</div>
                    <div className="costrans_toast-message">{toast.message}</div>
                </div>
                <button className="costrans_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <X size={16} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// CUSTOM DROPDOWN COMPONENT (Main dropdown)
// ==============================================
const CustomDropdown = ({ label, icon: Icon, value, onChange, options, placeholder, translations, disabled = false, instanceId = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isHovering, setIsHovering] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    const dropdownId = instanceId || label;

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        if (!isHovering) {
            setSearchTerm(displayValue);
        }
    }, [displayValue, isHovering]);

    useEffect(() => {
        if (isOpen) {
            const event = new CustomEvent('dropdownOpened', { detail: { id: dropdownId } });
            window.dispatchEvent(event);
        }
    }, [isOpen, dropdownId]);

    useEffect(() => {
        const handleDropdownOpened = (event) => {
            if (event.detail && event.detail.id !== dropdownId) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        window.addEventListener('dropdownOpened', handleDropdownOpened);
        return () => window.removeEventListener('dropdownOpened', handleDropdownOpened);
    }, [dropdownId]);

    const filteredOptions = isHovering
        ? options
        : searchTerm.trim() === ''
            ? options
            : options.filter(opt =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase())
            );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        setHighlightedIndex(-1);
        setIsHovering(false);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
                setIsHovering(false);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            default:
                break;
        }
    };

    const handleMouseEnter = () => {
        if (disabled) return;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setIsHovering(true);
            setIsOpen(true);
            setHighlightedIndex(0);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setTimeout(() => {
            if (!isOpen) {
                setIsHovering(false);
            }
        }, 300);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.costrans-custom-dropdown-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    const handleInputClick = () => {
        if (disabled) return;
        setIsHovering(false);
        setSearchTerm(displayValue);
        setIsOpen(true);
        setHighlightedIndex(0);
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setIsHovering(false);
        setSearchTerm(newValue);
        setIsOpen(true);
        setHighlightedIndex(0);
        if (!options.find(opt => opt.label.toLowerCase() === newValue.toLowerCase())) {
            onChange('');
        }
    };

    return (
        <div
            className="costrans-custom-dropdown-wrapper"
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="costrans-custom-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                    onFocus={() => {
                        if (!disabled) {
                            setIsHovering(false);
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {Icon && <Icon size={16} style={{ marginRight: '6px' }} />}
                    {label}
                </label>
                <ChevronDown size={14} className="costrans-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="costrans-custom-dropdown-menu" ref={menuRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => {
                            const IconComponent = opt.icon;
                            const ArrowIcon = opt.isCredit ? ArrowDownCircle : (opt.isDebit || opt.isOutgoing ? ArrowUpCircle : null);
                            return (
                                <div
                                    key={opt.value}
                                    className={`costrans-custom-dropdown-option ${value === opt.value ? 'costrans-selected' : ''} ${highlightedIndex === index ? 'costrans-highlighted' : ''}`}
                                    onClick={() => handleSelect(opt)}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                >
                                    <div className="costrans-dropdown-option-left">
                                        {IconComponent && (
                                            <IconComponent 
                                                size={20} 
                                                className="costrans-dropdown-option-icon"
                                                style={{ color: opt.color || 'var(--costrans-primary)' }}
                                            />
                                        )}
                                        <div className="costrans-dropdown-option-text">
                                            <span className="costrans-dropdown-option-label">{opt.label}</span>
                                            {opt.description && (
                                                <span className="costrans-dropdown-option-desc">{opt.description}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="costrans-dropdown-option-right">
                                        {ArrowIcon && (
                                            <ArrowIcon 
                                                size={16} 
                                                className={`costrans-dropdown-arrow-indicator ${opt.isCredit ? 'costrans-incoming' : 'costrans-outgoing'}`}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="costrans-custom-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ==============================================
// DAILY OPENING/CLOSING SECTION COMPONENT
// ==============================================
const DailyOpeningClosingSection = ({ 
    dailyData, 
    setDailyData, 
    loading, 
    t, 
    isRTL,
    onSave,
    saving 
}) => {
    const handleChange = useCallback((field, value) => {
        setDailyData(prev => ({ ...prev, [field]: value }));
    }, [setDailyData]);

    const accounts = Array.isArray(dailyData.accounts) ? dailyData.accounts : [];

    const addAccount = useCallback((type) => {
        setDailyData(prev => ({
            ...prev,
            accounts: [...(prev.accounts || []), createDailyAccount(type)]
        }));
    }, [setDailyData]);

    const updateAccount = useCallback((id, field, value) => {
        setDailyData(prev => ({
            ...prev,
            accounts: (prev.accounts || []).map(account =>
                account.id === id ? { ...account, [field]: value } : account
            )
        }));
    }, [setDailyData]);

    const removeAccount = useCallback((id) => {
        setDailyData(prev => ({
            ...prev,
            accounts: (prev.accounts || []).filter(account => account.id !== id)
        }));
    }, [setDailyData]);

    const renderOpeningClosingPair = (label, openingKey, closingKey, IconComponent, color = 'var(--costrans-primary)') => (
        <div className="costrans-oc-pair">
            <div className="costrans-oc-label">
                <IconComponent size={16} style={{ color }} />
                <span>{label}</span>
            </div>
            <div className="costrans-oc-inputs">
                <div className="costrans-oc-input-wrapper">
                    <span className="costrans-oc-input-label">{t('opening')}</span>
                    <input
                        type="number"
                        step="0.01"
                        value={dailyData[openingKey] ?? ''}
                        onChange={e => handleChange(openingKey, e.target.value)}
                        placeholder="0.00"
                        disabled={loading}
                        style={{ borderColor: 'var(--costrans-success)' }}
                    />
                </div>
                <div className="costrans-oc-input-wrapper">
                    <span className="costrans-oc-input-label">{t('closing')}</span>
                    <input
                        type="number"
                        step="0.01"
                        value={dailyData[closingKey] ?? ''}
                        onChange={e => handleChange(closingKey, e.target.value)}
                        placeholder="0.00"
                        disabled={loading}
                        style={{ borderColor: 'var(--costrans-error)' }}
                    />
                </div>
            </div>
        </div>
    );

    const totals = getDailyTotals(dailyData);

    const renderAccountSection = (type, title, IconComponent, color) => {
        const providerOptions = type === 'bank' ? PAKISTAN_BANKS : PAKISTAN_DIGITAL_WALLETS;
        const dropdownOptions = providerOptions.map(provider => ({
            value: provider,
            label: provider,
            icon: IconComponent,
            color
        }));
        const customValue = type === 'bank' ? 'Other / Custom Bank' : 'Other / Custom Wallet';
        const typeAccounts = accounts.filter(account => account.type === type);
        return (
            <section className="costrans-dynamic-accounts">
                <div className="costrans-dynamic-accounts-header">
                    <div><IconComponent size={18} style={{ color }} /><strong>{title}</strong><span>{typeAccounts.length} account{typeAccounts.length === 1 ? '' : 's'}</span></div>
                    <button type="button" className="costrans-btn costrans-btn-primary costrans-dynamic-add-btn" onClick={() => addAccount(type)} disabled={loading}>
                        <Plus size={16} /> Add {type === 'bank' ? 'Bank Account' : 'Digital Wallet'}
                    </button>
                </div>
                {typeAccounts.length === 0 ? (
                    <div className="costrans-dynamic-empty">No {type === 'bank' ? 'bank accounts' : 'digital wallets'} added. Add as many as your shop uses.</div>
                ) : (
                    <div className="costrans-dynamic-list">
                        {typeAccounts.map((account, index) => (
                            <div className={`costrans-dynamic-row ${account.provider_name === customValue ? 'has-custom-provider' : ''}`} key={account.id}>
                                <span className="costrans-dynamic-number">{index + 1}</span>
                                <div className="costrans-dynamic-provider-field">
                                    <CustomDropdown
                                        instanceId={`daily-${type}-${account.id}`}
                                        label={type === 'bank' ? 'Select Bank' : 'Select Digital Wallet'}
                                        icon={IconComponent}
                                        value={account.provider_name || ''}
                                        onChange={value => updateAccount(account.id, 'provider_name', value)}
                                        options={dropdownOptions}
                                        disabled={loading}
                                    />
                                </div>
                                {account.provider_name === customValue && (
                                    <div className="costrans-custom-input-wrapper costrans-dynamic-text-field">
                                        <input value={account.custom_provider || ''} onChange={event => updateAccount(account.id, 'custom_provider', event.target.value)} placeholder=" " disabled={loading} />
                                        <label>Provider name</label>
                                    </div>
                                )}
                                <div className="costrans-custom-input-wrapper costrans-dynamic-text-field">
                                    <input value={account.account_label || ''} onChange={event => updateAccount(account.id, 'account_label', event.target.value)} placeholder=" " disabled={loading} />
                                    <label>Account label</label>
                                </div>
                                <div className="costrans-custom-input-wrapper costrans-dynamic-text-field">
                                    <input value={account.account_number || ''} onChange={event => updateAccount(account.id, 'account_number', event.target.value)} placeholder=" " disabled={loading} />
                                    <label>{type === 'bank' ? 'Account / IBAN' : 'Mobile / Account'}</label>
                                </div>
                                <div className="costrans-custom-input-wrapper costrans-dynamic-text-field costrans-dynamic-money-field">
                                    <input type="number" step="0.01" value={account.opening ?? ''} onChange={event => updateAccount(account.id, 'opening', event.target.value)} placeholder=" " disabled={loading} />
                                    <label>Opening</label>
                                </div>
                                <div className="costrans-custom-input-wrapper costrans-dynamic-text-field costrans-dynamic-money-field">
                                    <input type="number" step="0.01" value={account.closing ?? ''} onChange={event => updateAccount(account.id, 'closing', event.target.value)} placeholder=" " disabled={loading} />
                                    <label>Closing</label>
                                </div>
                                <button type="button" className="costrans-dynamic-remove" onClick={() => removeAccount(account.id)} title="Remove account" disabled={loading}><Trash2 size={16} /></button>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        );
    };

    return (
        <div className="costrans-daily-oc-section">
            <div className="costrans-section-header costrans-oc-header">
                <Clock size={16} />
                <span>{t('dailyOpeningClosing')}</span>
            </div>

            <div className="costrans-oc-grid">
                {renderOpeningClosingPair(t('cash'), 'cash_opening', 'cash_closing', DollarSign, '#10b981')}
                {renderOpeningClosingPair(t('cheque'), 'cheque_opening', 'cheque_closing', FileText, '#8b5cf6')}
            </div>

            {renderAccountSection('bank', 'Bank Accounts', Building2, '#3b82f6')}
            {renderAccountSection('wallet', 'Digital Wallets', Wallet, '#f59e0b')}

            <div className="costrans-oc-totals">
                <div className="costrans-total-item costrans-total-incoming">
                    <span className="costrans-total-label">Total Opening</span>
                    <span className="costrans-total-value">Rs. {formatCurrency(totals.opening)}</span>
                </div>
                <div className="costrans-total-item costrans-total-outgoing">
                    <span className="costrans-total-label">Total Closing</span>
                    <span className="costrans-total-value">Rs. {formatCurrency(totals.closing)}</span>
                </div>
                <div className={`costrans-total-item costrans-total-net ${totals.net >= 0 ? 'costrans-positive' : 'costrans-negative'}`}>
                    <span className="costrans-total-label">{t('netBalance')}</span>
                    <span className="costrans-total-value">{totals.net >= 0 ? '+' : ''}Rs. {formatCurrency(totals.net)}</span>
                </div>
            </div>

            <div className="costrans-daily-save-actions">
                <button
                    className="costrans-btn costrans-btn-primary"
                    onClick={onSave}
                    disabled={loading || saving}
                >
                    {saving ? (
                        <>
                            <Loader2 size={16} className="costrans-spin" />
                            <span>{t('saving')}</span>
                        </>
                    ) : (
                        <>
                            <Save size={16} />
                            <span>{t('saveDailySummary')}</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

// ==============================================
// REFERENCE ITEM COMPONENT
// ==============================================
const ReferenceItem = ({ item, isSelected, onClick, showBalance = true, balanceLabel = '', entityType = '' }) => (
    <div 
        className={`costrans-ref-item ${isSelected ? 'costrans-selected' : ''}`}
        onClick={onClick}
    >
        <div className="costrans-ref-info">
            <span className="costrans-ref-name">{item.name}</span>
            {item.phone && <span className="costrans-ref-detail">{item.phone}</span>}
        </div>
        {showBalance && item.balance !== undefined && (
            <div className="costrans-ref-balance">
                <span className={`costrans-balance-amount ${parseFloat(item.balance) > 0 ? 'costrans-has-due' : 'costrans-no-due'}`}>
                    {balanceLabel && <span className="costrans-balance-label">{getSimpleBalanceLabel(entityType, item.balance, balanceLabel)}: </span>}
                    Rs. {formatCurrency(item.balance || 0)}
                </span>
            </div>
        )}
        {isSelected && (
            <div className="costrans-selected-check">
                <Check size={18} className="costrans-check-icon" />
            </div>
        )}
    </div>
);

// ==============================================
// INVOICE ITEM COMPONENT
// ==============================================
const InvoiceItem = ({ invoice, isSelected, onClick, t }) => {
    const dueAmount = parseFloat(invoice.balance || invoice.amount_due || 0);
    const totalAmount = parseFloat(invoice.amount || invoice.grand_total || 0);
    const paidAmount = totalAmount - dueAmount;
    const isPaid = dueAmount <= 0.01;
    const isPartial = !isPaid && paidAmount > 0.01;

    let StatusIcon = Circle;
    let statusColor = 'var(--costrans-error)';
    let statusText = t('pending');
    let statusClass = 'pending';

    if (isPaid) {
        StatusIcon = CheckCheck;
        statusColor = 'var(--costrans-success)';
        statusText = t('paid');
        statusClass = 'paid';
    } else if (isPartial) {
        StatusIcon = AlertCircle;
        statusColor = 'var(--costrans-warning)';
        statusText = t('partiallyPaid');
        statusClass = 'partial';
    }

    return (
        <div
            className={`costrans-invoice-item ${isSelected ? 'costrans-selected' : ''}`}
            onClick={onClick}
        >
            <div className="costrans-invoice-checkbox">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
            <div className="costrans-invoice-info">
                <span className="costrans-invoice-number">
                    <FileText size={12} /> {invoice.invoice_number || `INV-${invoice.purchase_id || invoice.transaction_id}`}
                </span>
                <span className="costrans-invoice-date">
                    {new Date(invoice.transaction_date || invoice.created_at).toLocaleDateString()}
                </span>
            </div>
            <div className="costrans-invoice-amounts">
                <span className="costrans-invoice-total">Rs. {formatCurrency(totalAmount)}</span>
                {!isPaid && (
                    <span className={`costrans-invoice-due ${statusClass}`}>
                        {t('due')}: Rs. {formatCurrency(dueAmount)}
                    </span>
                )}
                {isPaid && (
                    <span className="costrans-invoice-due paid">
                        {t('paid')}: Rs. {formatCurrency(totalAmount)}
                    </span>
                )}
            </div>
            <div className="costrans-invoice-status">
                <StatusIcon 
                    size={14} 
                    className={`costrans-status-icon ${statusClass}`}
                    style={{ color: statusColor }}
                />
                <span className={`costrans-status-badge ${statusClass}`}>
                    {statusText}
                </span>
            </div>
        </div>
    );
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const AddTransaction = () => {
    const { user, currentShop } = useAuth();

    // ── State ──────────────────────────────────────────────────
    const [selectedOption, setSelectedOption] = useState('daily_opening_closing');
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [savingDaily, setSavingDaily] = useState(false);

    // ── Daily Opening/Closing State ──────────────────────────
    const [dailyData, setDailyData] = useState({
        cash_opening: 0,
        cash_closing: 0,
        cheque_opening: 0,
        cheque_closing: 0,
        accounts: []
    });

    // ── Selected Reference and Invoices ──────────────────────
    const [selectedReference, setSelectedReference] = useState(null);
    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentEntryMode, setPaymentEntryMode] = useState('regular');
    const [isFullPayment, setIsFullPayment] = useState(true);
    const [referenceBalance, setReferenceBalance] = useState(0);
    const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');

    // ── Form Data ─────────────────────────────────────────────
    const [formData, setFormData] = useState({
        reference_id: '',
        amount: '',
        transaction_date: new Date().toISOString().split('T')[0],
        description: '',
        payment_method: 'cash',
        invoice_number: '',
        expense_category: '',
        expense_name: ''
    });

    // ── Language & Theme ──────────────────────────────────────
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });

    // ── Refs ──────────────────────────────────────────────────
    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);
    const fetchedRefs = useRef(false);
    const leftColumnRef = useRef(null);

    // ── Translation helper ──────────────────────────────────
    const t = useCallback((key) => {
        const lang = currentLanguage in translations ? currentLanguage : 'en';
        return translations[lang][key] || key;
    }, [currentLanguage]);

    // ── Toast ──────────────────────────────────────────────────
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    // ── Payment Method Options - DEFINED INSIDE COMPONENT ──
    const paymentMethodOptions = useMemo(() => [
        { value: 'cash', label: t('cash'), icon: DollarSign },
        { value: 'bank_transfer', label: t('bankTransfer'), icon: Building2 },
        { value: 'cheque', label: t('cheque'), icon: FileText },
        { value: 'credit_card', label: t('creditCard'), icon: CreditCard },
        { value: 'mobile_payment', label: t('mobilePayment'), icon: PhoneIcon }
    ], [t]);

    // ── Dropdown options with Lucide icons ──────────────────
    const dropdownOptions = useMemo(() => [
        { 
            value: 'daily_opening_closing', 
            label: t('dailyOpeningClosing'), 
            icon: Clock, 
            color: '#8b5cf6',
            isDaily: true,
            description: t('dailyOpeningDesc')
        },
        { 
            value: 'customer_payment', 
            label: t('customerPayment'), 
            icon: Users, 
            color: '#10b981',
            type: 'customer',
            isCredit: true,
            showInvoices: true,
            balanceLabel: t('customerOwes'),
            action: 'add_payment',
            description: t('customerPaymentDesc')
        },
        { 
            value: 'customer_due', 
            label: t('customerDue'), 
            icon: Users, 
            color: '#ef4444',
            type: 'customer',
            isDebit: true,
            showInvoices: false,
            balanceLabel: t('customerOwes'),
            action: 'add_due',
            description: t('customerDueDesc')
        },
        { 
            value: 'supplier_payment', 
            label: t('supplierPayment'), 
            icon: Truck, 
            color: '#ef4444',
            type: 'supplier',
            isDebit: true,
            showInvoices: true,
            balanceLabel: t('youOweSupplier'),
            action: 'add_supplier_payment',
            description: t('supplierPaymentDesc')
        },
        { 
            value: 'supplier_due', 
            label: t('supplierDue'), 
            icon: Truck, 
            color: '#f59e0b',
            type: 'supplier',
            isDebit: true,
            showInvoices: false,
            balanceLabel: t('youOweSupplier'),
            action: 'add_supplier_due',
            description: t('supplierDueDesc')
        },
        { 
            value: 'partner_investment', 
            label: t('partnerInvestment'), 
            icon: Handshake, 
            color: '#10b981',
            type: 'partner',
            isCredit: true,
            showInvoices: false,
            balanceLabel: t('balance'),
            action: 'add_partner_credit',
            description: t('partnerInvestmentDesc')
        },
        { 
            value: 'partner_payment', 
            label: t('partnerPayment'), 
            icon: Handshake, 
            color: '#ef4444',
            type: 'partner',
            isDebit: true,
            showInvoices: false,
            balanceLabel: t('balance'),
            action: 'add_partner_debit',
            description: t('partnerPaymentDesc')
        },
        { 
            value: 'employee_salary', 
            label: t('employeeSalary'), 
            icon: UserCircle, 
            color: '#ef4444',
            type: 'employee',
            isDebit: true,
            isOutgoing: true,
            showInvoices: false,
            balanceLabel: t('balance'),
            action: 'add_employee_salary',
            description: t('employeeSalaryDesc')
        },
        { 
            value: 'expense', 
            label: t('shopExpense'), 
            icon: Receipt, 
            color: '#f59e0b',
            type: 'expense',
            isOutgoing: true,
            showInvoices: false,
            action: 'add_expense',
            description: t('expenseDesc')
        },
    ], [t]);

    // ── Get current option details ──────────────────────────
    const currentOption = useMemo(() => {
        return dropdownOptions.find(opt => opt.value === selectedOption);
    }, [selectedOption, dropdownOptions]);

    const isDaily = selectedOption === 'daily_opening_closing';
    const isExpense = selectedOption === 'expense';
    const isCredit = currentOption?.isCredit;
    const isDebit = currentOption?.isDebit;
    const isOutgoing = currentOption?.isOutgoing;
    const advanceOptionConfig = useMemo(() => ({
        customer_payment: {
            action: 'add_advance_received_customer',
            entryMode: 'advance_received_customer',
            label: t('advanceReceivedCustomer'),
            hint: t('advanceReceivedCustomerHint'),
            notice: 'Customer paid in advance. I may need to provide goods or return this money.',
            direction: 'incoming'
        },
        supplier_payment: {
            action: 'add_advance_paid_supplier',
            entryMode: 'advance_paid_supplier',
            label: t('advancePaidSupplier'),
            hint: t('advancePaidSupplierHint'),
            notice: 'I paid the supplier in advance. The supplier must provide goods or return this money.',
            direction: 'outgoing'
        },
        customer_due: {
            action: 'add_advance_return_customer',
            entryMode: 'advance_return_customer',
            label: t('advanceReturnCustomer'),
            hint: t('advanceReturnCustomerHint'),
            notice: 'I am returning unused advance money to the customer.',
            direction: 'outgoing'
        },
        supplier_due: {
            action: 'add_advance_return_supplier',
            entryMode: 'advance_return_supplier',
            label: t('advanceReturnSupplier'),
            hint: t('advanceReturnSupplierHint'),
            notice: 'The supplier is returning my unused advance money.',
            direction: 'incoming'
        }
    }), [t]);
    const advanceConfig = advanceOptionConfig[selectedOption] || null;
    const isAdvanceEligible = Boolean(advanceConfig);
    const isAdvanceMode = isAdvanceEligible && paymentEntryMode === 'advance';
    const showInvoices = currentOption?.showInvoices && selectedReference && !isDaily && !isAdvanceMode;
    const balanceLabel = currentOption?.balanceLabel || '';
    const currentType = currentOption?.type;

    // ── Reset form ────────────────────────────────────────────
    const resetForm = useCallback(() => {
        setFormData({
            reference_id: '',
            amount: '',
            transaction_date: new Date().toISOString().split('T')[0],
            description: '',
            payment_method: 'cash',
            invoice_number: '',
            expense_category: '',
            expense_name: ''
        });
        setSelectedReference(null);
        setSelectedInvoices([]);
        setPaymentAmount('');
        setPaymentEntryMode('regular');
        setIsFullPayment(true);
        setUnpaidInvoices([]);
        setReferenceBalance(0);
        setSearchTerm('');
        setInvoiceSearchTerm('');
    }, []);

    // ── Load reference list with balances ────────────────────
    const loadReferenceList = useCallback(async (type) => {
        if (!currentShop) return;
        try {
            let api = '';
            let idField = '';
            let nameField = '';
            let balanceField = 'balance';

            switch (type) {
                case 'customer':
                    api = CUSTOMERS_API;
                    idField = 'customerid';
                    nameField = 'customer_name';
                    balanceField = 'total_due';
                    break;
                case 'supplier':
                    api = SUPPLIERS_API;
                    idField = 'supplierid';
                    nameField = 'supplier_name';
                    balanceField = 'current_balance';
                    break;
                case 'employee':
                    api = EMPLOYEES_API;
                    idField = 'employee_id';
                    nameField = 'employee_name';
                    balanceField = 'balance';
                    break;
                case 'partner':
                    api = PARTNERS_API;
                    idField = 'partner_id';
                    nameField = 'partner_name';
                    balanceField = 'balance';
                    break;
                default:
                    return;
            }

            if (type === 'supplier') {
                const res = await fetch(TRANSACTIONS_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_all_suppliers',
                        shop_id: currentShop.shop_id
                    })
                });
                const data = await res.json();
                if (data.status === 'success') {
                    const items = (data.data || []).map(item => ({
                        id: item.id || item.supplierid,
                        name: item.name || item.supplier_name,
                        phone: item.phone,
                        email: item.email,
                        balance: parseFloat(item.balance || 0)
                    }));
                    items.sort((a, b) => (b.balance || 0) - (a.balance || 0));
                    setSuppliers(items);
                }
                return;
            }

            const requestBody = {
                action: 'get_records',
                shop_id: currentShop.shop_id
            };

            // For partners API, use 'get_partners' action instead of 'get_records'
            if (type === 'partner') {
                requestBody.action = 'get_partners';
            }

            const res = await fetch(api, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });
            const data = await res.json();

            if (data.status === 'success') {
                let items = [];
                
                // Handle different response structures
                if (type === 'partner') {
                    // Partners API returns data in 'data' array
                    items = (data.data || []).map(item => ({
                        id: item[idField] || item.partner_id || item.id,
                        name: item[nameField] || item.partner_name || item.name,
                        phone: item.phone || item.phone_number || '',
                        email: item.email || '',
                        balance: parseFloat(item[balanceField] || 0)
                    }));
                } else {
                    // Employees API and others return data in 'data' array with different field names
                    items = (data.data || []).map(item => ({
                        id: item[idField] || item.id,
                        name: item[nameField] || item.name,
                        phone: item.phone || item.phonenumber || '',
                        email: item.email || '',
                        balance: parseFloat(item[balanceField] || 0)
                    }));
                }

                items.sort((a, b) => (b.balance || 0) - (a.balance || 0));

                switch (type) {
                    case 'customer':
                        setCustomers(items);
                        break;
                    case 'employee':
                        setEmployees(items);
                        break;
                    case 'partner':
                        setPartners(items);
                        break;
                    default:
                        break;
                }
            }
        } catch (err) {
            console.warn(`Failed to load ${type}s:`, err);
        }
    }, [currentShop]);

    // ── Load unpaid invoices for reference ───────────────────
    const loadUnpaidInvoices = useCallback(async (referenceId, type) => {
        if (!currentShop || !referenceId) {
            setUnpaidInvoices([]);
            setReferenceBalance(0);
            return;
        }

        setLoadingInvoices(true);
        try {
            if (type === 'supplier') {
                const res = await fetch(TRANSACTIONS_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_supplier_unpaid_invoices',
                        shop_id: currentShop.shop_id,
                        supplier_id: referenceId
                    })
                });
                const data = await res.json();
                if (data.status === 'success') {
                    setUnpaidInvoices(data.data || []);
                    setReferenceBalance(data.total_balance || 0);
                } else {
                    setUnpaidInvoices([]);
                    setReferenceBalance(0);
                }
            } else if (type === 'customer') {
                const res = await fetch(TRANSACTIONS_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_customer_profile',
                        shop_id: currentShop.shop_id,
                        customer_id: referenceId
                    })
                });
                const data = await res.json();
                if (data.status === 'success') {
                    const unpaid = (data.data?.transactions || []).filter(tx => 
                        parseFloat(tx.amount_due || tx.balance || 0) > 0.01 &&
                        (tx.transaction_type === 'sale' || tx.transaction_type === 'due')
                    );
                    setUnpaidInvoices(unpaid);
                    setReferenceBalance(data.data?.summary?.total_due || 0);
                } else {
                    setUnpaidInvoices([]);
                    setReferenceBalance(0);
                }
            } else {
                // For employees and partners, we may not have invoice tracking
                setUnpaidInvoices([]);
                setReferenceBalance(0);
            }
        } catch (err) {
            console.warn('Failed to load invoices:', err);
            setUnpaidInvoices([]);
            setReferenceBalance(0);
        } finally {
            setLoadingInvoices(false);
        }
    }, [currentShop]);

    // ── Load all data on mount ───────────────────────────────
    useEffect(() => {
        if (currentShop && !fetchedRefs.current) {
            fetchedRefs.current = true;
            loadReferenceList('customer');
            loadReferenceList('supplier');
            loadReferenceList('employee');
            loadReferenceList('partner');
        }
    }, [currentShop, loadReferenceList]);

    // ── Reset form when dropdown changes ─────────────────────
    useEffect(() => {
        resetForm();
    }, [selectedOption, resetForm]);

    // ── Handle dropdown change ───────────────────────────────
    const handleOptionChange = useCallback((value) => {
        setSelectedOption(value);
    }, []);

    // ── Handle reference selection ───────────────────────────
    const handleReferenceSelect = useCallback((reference) => {
        setSelectedReference(reference);
        setFormData(prev => ({ ...prev, reference_id: reference.id }));
        setSelectedInvoices([]);
        setPaymentAmount('');
        
        if (currentType && !isDaily && !isExpense) {
            if (currentOption?.showInvoices) {
                loadUnpaidInvoices(reference.id, currentType);
            } else {
                setUnpaidInvoices([]);
                setReferenceBalance(reference.balance || 0);
            }
        }
    }, [currentType, currentOption, isDaily, isExpense, loadUnpaidInvoices]);

    // ── Toggle invoice selection ─────────────────────────────
    const toggleInvoiceSelection = useCallback((invoice) => {
        setSelectedInvoices(prev => {
            const exists = prev.find(inv => (inv.transaction_id || inv.purchase_id) === (invoice.transaction_id || invoice.purchase_id));
            if (exists) {
                return prev.filter(inv => (inv.transaction_id || inv.purchase_id) !== (invoice.transaction_id || invoice.purchase_id));
            } else {
                return [...prev, invoice];
            }
        });
    }, []);

    // ── Calculate selected total ─────────────────────────────
    const getSelectedTotal = useCallback(() => {
        return selectedInvoices.reduce((sum, inv) => {
            const balance = parseFloat(inv.balance || inv.amount_due || 0);
            return sum + balance;
        }, 0);
    }, [selectedInvoices]);

    // ── Get filtered invoices ────────────────────────────────
    const getFilteredInvoices = useCallback(() => {
        let list = unpaidInvoices;
        const search = invoiceSearchTerm.toLowerCase().trim();

        if (search) {
            list = list.filter(inv =>
                (inv.invoice_number || '').toLowerCase().includes(search) ||
                (inv.transaction_id || '').toString().includes(search) ||
                (inv.purchase_id || '').toString().includes(search)
            );
        }

        return list.sort((a, b) => {
            const aBalance = parseFloat(a.balance || a.amount_due || 0);
            const bBalance = parseFloat(b.balance || b.amount_due || 0);
            return aBalance - bBalance;
        });
    }, [unpaidInvoices, invoiceSearchTerm]);

    // ── Select/Deselect all invoices ─────────────────────────
    const selectAllInvoices = useCallback(() => {
        const filtered = getFilteredInvoices();
        if (selectedInvoices.length === filtered.length && filtered.length > 0) {
            setSelectedInvoices([]);
        } else {
            setSelectedInvoices([...filtered]);
        }
    }, [selectedInvoices, getFilteredInvoices]);

    // ── Handle payment amount change ──────────────────────────
    const handlePaymentAmountChange = useCallback((value) => {
        setPaymentAmount(value);
        setFormData(prev => ({ ...prev, amount: value }));
        
        const selectedTotal = getSelectedTotal();
        const enteredAmount = parseFloat(value) || 0;
        setIsFullPayment(enteredAmount >= selectedTotal - 0.01);
    }, [getSelectedTotal]);

    // ── Get filtered reference list ──────────────────────────
    const getFilteredReferences = useCallback(() => {
        let list = [];
        const search = searchTerm.toLowerCase().trim();

        switch (currentType) {
            case 'customer':
                list = customers;
                break;
            case 'supplier':
                list = suppliers;
                break;
            case 'employee':
                list = employees;
                break;
            case 'partner':
                list = partners;
                break;
            default:
                return [];
        }

        if (search) {
            list = list.filter(item =>
                (item.name || '').toLowerCase().includes(search) ||
                (item.phone || '').includes(search) ||
                (item.email || '').toLowerCase().includes(search)
            );
        }

        return list;
    }, [currentType, customers, suppliers, employees, partners, searchTerm]);

    // ── Get reference label ──────────────────────────────────
    const getReferenceLabel = useCallback(() => {
        switch (currentType) {
            case 'customer': return t('customer');
            case 'supplier': return t('supplier');
            case 'employee': return t('employee');
            case 'partner': return t('partner');
            default: return t('reference');
        }
    }, [currentType, t]);

    // ── Get action for API ────────────────────────────────────
    const getActionForOption = useCallback(() => {
        return currentOption?.action || 'add_transaction';
    }, [currentOption]);

    // ── Save Daily Summary ────────────────────────────────────
    const handleSaveDailySummary = useCallback(async () => {
        if (!currentShop) {
            showToast('error', t('selectShopFirst'));
            return;
        }

        setSavingDaily(true);
        try {
            const invalidAccount = (dailyData.accounts || []).find(account =>
                !String(account.provider_name || '').trim() ||
                (/^Other \/ Custom/.test(account.provider_name) && !String(account.custom_provider || '').trim())
            );
            if (invalidAccount) {
                showToast('error', `Please select or enter the ${invalidAccount.type === 'wallet' ? 'wallet' : 'bank'} name`);
                return;
            }

            const accounts = (dailyData.accounts || []).map(account => ({
                id: account.id,
                type: account.type,
                provider_name: /^Other \/ Custom/.test(account.provider_name)
                    ? String(account.custom_provider || '').trim()
                    : String(account.provider_name || '').trim(),
                account_label: String(account.account_label || '').trim(),
                account_number: String(account.account_number || '').trim(),
                opening: parseFloat(account.opening) || 0,
                closing: parseFloat(account.closing) || 0
            }));
            const bankAccounts = accounts.filter(account => account.type === 'bank');
            const walletAccounts = accounts.filter(account => account.type === 'wallet');

            const body = {
                action: 'save_daily_summary',
                shop_id: currentShop.shop_id,
                username: user?.username || 'system',
                summary_date: new Date().toISOString().split('T')[0],
                cash_opening: parseFloat(dailyData.cash_opening) || 0,
                cash_closing: parseFloat(dailyData.cash_closing) || 0,
                bank_opening: bankAccounts.reduce((sum, account) => sum + account.opening, 0),
                bank_closing: bankAccounts.reduce((sum, account) => sum + account.closing, 0),
                wallet_opening: walletAccounts.reduce((sum, account) => sum + account.opening, 0),
                wallet_closing: walletAccounts.reduce((sum, account) => sum + account.closing, 0),
                cheque_opening: parseFloat(dailyData.cheque_opening) || 0,
                cheque_closing: parseFloat(dailyData.cheque_closing) || 0,
                accounts
            };

            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();

            if (data.status === 'success') {
                showToast('success', t('dailySummarySaved'));
            } else {
                showToast('error', data.message || 'Failed to save daily summary');
            }
        } catch (err) {
            showToast('error', 'Error saving daily summary');
        } finally {
            setSavingDaily(false);
        }
    }, [currentShop, dailyData, user, showToast, t]);

    // ── Add transaction ───────────────────────────────────────
    // Load today's saved values before editing so reopening this screen cannot
    // overwrite an existing Daily O/C with an empty form.
    useEffect(() => {
        if (!isDaily || !currentShop) return undefined;
        let active = true;
        const loadTodaySummary = async () => {
            try {
                const response = await fetch(TRANSACTIONS_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_daily_summary',
                        shop_id: currentShop.shop_id,
                        summary_date: new Date().toISOString().split('T')[0]
                    })
                });
                const result = await response.json();
                if (!active || result.status !== 'success') return;
                const summary = result.data || {};
                setDailyData({
                    cash_opening: Number.parseFloat(summary.cash_opening) || 0,
                    cash_closing: Number.parseFloat(summary.cash_closing) || 0,
                    cheque_opening: Number.parseFloat(summary.cheque_opening) || 0,
                    cheque_closing: Number.parseFloat(summary.cheque_closing) || 0,
                    accounts: normalizeDailyAccounts(summary.accounts, summary)
                });
            } catch (error) {
                console.warn('Could not load today\'s Daily O/C:', error);
            }
        };
        loadTodaySummary();
        return () => { active = false; };
    }, [isDaily, currentShop]);

    const handleAddTransaction = useCallback(async () => {
        if (isSubmittingRef.current) return;
        if (!currentShop) {
            showToast('error', t('selectShopFirst'));
            return;
        }

        if (isDaily) {
            return;
        }

        // Validation
        if (!isExpense) {
            if (!selectedReference) {
                showToast('error', t('select') + ' ' + getReferenceLabel());
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', t('amount') + ' required');
                return;
            }
        } else {
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', t('amount') + ' required');
                return;
            }
            // For expense, either category or name is required
            if (!formData.expense_category && !formData.expense_name) {
                showToast('error', t('expenseCategory') + ' or ' + t('expenseName') + ' required');
                return;
            }
        }

        if (isAdvanceMode && advanceConfig.entryMode.includes('advance_return')) {
            const availableAdvance = Math.max(0, -(Number.parseFloat(selectedReference?.balance) || 0));
            if (parseFloat(formData.amount) > availableAdvance + 0.005) {
                showToast('error', `Return amount cannot be more than the available advance: Rs. ${formatCurrency(availableAdvance)}`);
                return;
            }
        }

        isSubmittingRef.current = true;
        setLoading(true);

        try {
            const action = isAdvanceMode ? advanceConfig.action : getActionForOption();
            
            const refKeyMap = {
                customer: 'customer_id',
                supplier: 'supplier_id',
                partner: 'partner_id',
                employee: 'employee_id'
            };

            const body = {
                action,
                shop_id: currentShop.shop_id,
                username: user?.username || 'system',
                transaction_date: formData.transaction_date,
                payment_method: formData.payment_method,
                description: formData.description,
                amount: parseFloat(formData.amount)
            };

            if (isAdvanceMode) {
                body.is_advance = 1;
                body.entry_mode = advanceConfig.entryMode;
                body.reference_type = currentType;
                body.original_payment_option = selectedOption;
            }

            if (!isExpense && currentType) {
                const refKey = refKeyMap[currentType];
                if (refKey) {
                    body[refKey] = parseInt(selectedReference.id);
                }
            }

            if (isExpense) {
                // If category is selected but name is empty, use category label
                if (formData.expense_category && !formData.expense_name) {
                    const category = EXPENSE_CATEGORIES.find(cat => cat.value === formData.expense_category);
                    body.expense_name = category ? category.label : formData.expense_category;
                } else {
                    body.expense_name = formData.expense_name;
                }
                body.expense_category = formData.expense_category || 'General';
            }

            if (!isAdvanceMode && selectedOption === 'supplier_payment' && selectedInvoices.length > 0) {
                body.invoice_ids = selectedInvoices.map(inv => inv.purchase_id || inv.transaction_id);
                body.is_full_payment = isFullPayment ? 1 : 0;
            }

            if (formData.invoice_number) {
                body.invoice_number = formData.invoice_number;
            }

            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();

            if (data.status === 'success') {
                showToast('success', t('transactionSaved'));
                resetForm();
                loadReferenceList('customer');
                loadReferenceList('supplier');
                loadReferenceList('employee');
                loadReferenceList('partner');
            } else {
                showToast('error', data.message || t('failedSave'));
            }
        } catch (err) {
            showToast('error', err.message || t('failedSave'));
        } finally {
            setLoading(false);
            setTimeout(() => {
                isSubmittingRef.current = false;
            }, 500);
        }
    }, [currentShop, formData, selectedOption, user, showToast, resetForm, 
        t, getReferenceLabel, selectedReference, loadReferenceList, 
        selectedInvoices, isFullPayment, getActionForOption, isDaily, isExpense, currentType, isAdvanceMode, advanceConfig]);

    // ── Cleanup ───────────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // ── Theme and Dark Mode ──────────────────────────────────
    useEffect(() => {
        const applyTheme = (color) => {
            if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
                color = '#b3b910';
            }
            document.documentElement.style.setProperty('--costrans-primary', color);
            document.documentElement.style.setProperty('--costrans-primary-dark', color);
            document.documentElement.style.setProperty('--costrans-primary-light', color);
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            document.documentElement.style.setProperty('--costrans-primary-rgb', `${r}, ${g}, ${b}`);
        };

        const savedTheme = localStorage.getItem('ourzest_theme') || '#b3b910';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#b3b910');
            }
        };
        const handleThemeChange = (e) => {
            applyTheme(e.detail?.theme || localStorage.getItem('ourzest_theme') || '#b3b910');
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChange);
        window.addEventListener('themeUpdated', handleThemeChange);
        const interval = setInterval(() => {
            applyTheme(localStorage.getItem('ourzest_theme') || '#b3b910');
        }, 1000);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChange);
            window.removeEventListener('themeUpdated', handleThemeChange);
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        const syncDarkMode = () => {
            const stored = localStorage.getItem('ourzest_darkMode');
            const dashboardIsDark = document.body.classList.contains('dark-mode') ||
                document.body.classList.contains('dark') ||
                document.documentElement.dataset.theme === 'dark';
            setDarkMode(stored !== null ? stored === 'true' : dashboardIsDark);
        };

        syncDarkMode();
        const handleDarkStorage = (e) => {
            if (e.key === 'ourzest_darkMode') syncDarkMode();
        };
        const handleDarkToggle = (e) => {
            const nextDarkMode = e.detail?.darkMode;
            setDarkMode(typeof nextDarkMode === 'boolean'
                ? nextDarkMode
                : localStorage.getItem('ourzest_darkMode') === 'true');
        };

        window.addEventListener('storage', handleDarkStorage);
        window.addEventListener('darkModeToggled', handleDarkToggle);
        window.addEventListener('darkModeUpdated', handleDarkToggle);
        window.addEventListener('themeChanged', syncDarkMode);
        window.addEventListener('themeUpdated', syncDarkMode);
        const interval = setInterval(syncDarkMode, 1000);

        return () => {
            window.removeEventListener('storage', handleDarkStorage);
            window.removeEventListener('darkModeToggled', handleDarkToggle);
            window.removeEventListener('darkModeUpdated', handleDarkToggle);
            window.removeEventListener('themeChanged', syncDarkMode);
            window.removeEventListener('themeUpdated', syncDarkMode);
            clearInterval(interval);
        };
    }, []);

    // ── RTL ──────────────────────────────────────────────────
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    useEffect(() => {
        const container = document.querySelector('.costrans-container');
        if (container) {
            if (isRTL) {
                container.classList.add('costrans-rtl');
                container.classList.remove('costrans-ltr');
            } else {
                container.classList.add('costrans-ltr');
                container.classList.remove('costrans-rtl');
            }
        }
    }, [isRTL]);

    // ── Render ─────────────────────────────────────────────────
    const filteredReferences = getFilteredReferences();
    const filteredInvoices = getFilteredInvoices();
    const selectedTotal = getSelectedTotal();
    const remainingBalance = selectedTotal - (parseFloat(paymentAmount || formData.amount) || 0);
    const showReferenceList = !isDaily && !isExpense;

    return (
        <div className={`costrans-container ${isRTL ? 'costrans-rtl' : 'costrans-ltr'} ${darkMode ? 'costrans-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} t={t} />

            {/* Full-width transaction type tabs */}
            <nav className="costrans-transaction-tabs" aria-label="Transaction types">
                {dropdownOptions.map((option) => {
                    const TabIcon = option.icon;
                    const isActive = selectedOption === option.value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            className={`costrans-transaction-tab ${isActive ? 'active' : ''}`}
                            onClick={() => handleOptionChange(option.value)}
                            aria-current={isActive ? 'page' : undefined}
                            title={option.label}
                        >
                            <TabIcon className="costrans-transaction-tab-icon" aria-hidden="true" />
                            <span className="costrans-transaction-tab-label">{option.label}</span>
                        </button>
                    );
                })}
            </nav>

            {/* Two Column Layout */}
            <div className="costrans-two-column-layout">
                {/* Left Column */}
                <div
                    className="costrans-left-column"
                    ref={leftColumnRef}
                    onMouseEnter={() => {
                        if (leftColumnRef.current) {
                            leftColumnRef.current.style.overflowY = 'auto';
                        }
                    }}
                    onMouseLeave={() => {
                        if (leftColumnRef.current) {
                            leftColumnRef.current.style.overflowY = 'hidden';
                        }
                    }}
                >
                    

                    {/* Reference List - Only show for non-daily options */}
                    {showReferenceList && (
                        <div className="costrans-ref-list-section">
                            <div className="costrans-ref-search">
                                <Search size={16} className="costrans-search-icon" />
                                <input
                                    type="text"
                                    placeholder={t('searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="costrans-search-input"
                                />
                            </div>
                            <div className="costrans-ref-list">
                                {filteredReferences.length === 0 ? (
                                    <div className="costrans-no-results">
                                        <List size={32} />
                                        <span>{t('noResults')}</span>
                                    </div>
                                ) : (
                                    filteredReferences.map(item => (
                                        <ReferenceItem
                                            key={item.id}
                                            item={item}
                                            isSelected={selectedReference?.id === item.id}
                                            onClick={() => handleReferenceSelect(item)}
                                            showBalance={true}
                                            balanceLabel={balanceLabel}
                                            entityType={currentType}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Expense Prompt */}
                    {isExpense && (
                        <div className="costrans-ref-list-section costrans-expense-prompt">
                            <div className="costrans-select-prompt">
                                <Receipt size={20} className="costrans-prompt-icon" />
                                <span>{t('expenseDetails')}</span>
                            </div>
                        </div>
                    )}

                    {/* Daily Prompt */}
                    {isDaily && (
                        <div className="costrans-ref-list-section costrans-daily-prompt">
                            <div className="costrans-select-prompt">
                                <Clock size={20} className="costrans-prompt-icon" />
                                <span>{t('dailyOpeningClosing')}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column */}
                <div className="costrans-right-column">
                    {/* Daily Opening/Closing Section - ONLY SHOW when isDaily is true */}
                    {isDaily ? (
                        <DailyOpeningClosingSection
                            dailyData={dailyData}
                            setDailyData={setDailyData}
                            loading={loading}
                            t={t}
                            isRTL={isRTL}
                            onSave={handleSaveDailySummary}
                            saving={savingDaily}
                        />
                    ) : (
                        <>
                           
                            {/* Invoice Section (only for tabs that show invoices) */}
                            {!isExpense && showInvoices && (
                                <div className="costrans-invoice-section">
                                    <div className="costrans-section-header">
                                        <div className="costrans-header-left-section">
                                            <span className="costrans-badge">{unpaidInvoices.length}</span>
                                            <span>{t('unpaidInvoices')}</span>
                                            <span className="costrans-summary-value balance">Rs. {formatCurrency(referenceBalance)}</span>
                                        </div>
                                        <div className="costrans-header-right-section">
                                            <button
                                                className="costrans-select-all-btn"
                                                onClick={selectAllInvoices}
                                                disabled={unpaidInvoices.length === 0}
                                            >
                                                <CheckCheck size={14} />
                                                {selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0 ? t('deselectAll') : t('selectAll')}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="costrans-invoice-search">
                                        <input
                                            type="text"
                                            placeholder="Search invoices..."
                                            value={invoiceSearchTerm}
                                            onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                                            className="costrans-search-input"
                                        />
                                    </div>

                                    <div className="costrans-invoice-list">
                                        {loadingInvoices ? (
                                            <div className="costrans-loading-invoices">
                                                <Loader2 size={18} className="costrans-spin" />
                                                <span>{t('loadingInvoices')}</span>
                                            </div>
                                        ) : filteredInvoices.length === 0 ? (
                                            <div className="costrans-no-data">
                                                <CheckCircle size={18} />
                                                <span>{t('noUnpaidInvoices')}</span>
                                            </div>
                                        ) : (
                                            filteredInvoices.map(inv => (
                                                <InvoiceItem
                                                    key={inv.transaction_id || inv.purchase_id}
                                                    invoice={inv}
                                                    isSelected={selectedInvoices.some(i => (i.transaction_id || i.purchase_id) === (inv.transaction_id || inv.purchase_id))}
                                                    onClick={() => toggleInvoiceSelection(inv)}
                                                    t={t}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Selected Info for non-invoice tabs - compact version */}
                            {!isExpense && !showInvoices && selectedReference && (
                                <div className="costrans-selected-info">
                                    <div className="costrans-info-card">
                                        <div className="costrans-info-header">
                                            <User size={16} className="costrans-info-icon" />
                                            <span className="costrans-info-name">{selectedReference.name}</span>
                                            {balanceLabel && (
                                                <span className="costrans-info-balance-label">
                                                    {getSimpleBalanceLabel(currentType, selectedReference.balance, balanceLabel)}
                                                </span>
                                            )}
                                        </div>
                                        {selectedReference.phone && (
                                            <div className="costrans-info-row">
                                                <span className="costrans-info-label">{t('phone')}:</span>
                                                <span>{selectedReference.phone}</span>
                                            </div>
                                        )}
                                        {selectedReference.email && (
                                            <div className="costrans-info-row">
                                                <span className="costrans-info-label">{t('email')}:</span>
                                                <span>{selectedReference.email}</span>
                                            </div>
                                        )}
                                        {Math.abs(parseFloat(selectedReference.balance || 0)) > 0.01 && (
                                            <div className="costrans-info-row costrans-due-row">
                                                <span className="costrans-info-label">{t('balance')}:</span>
                                                <span className={`costrans-due-amount ${parseFloat(selectedReference.balance) > 0 ? 'costrans-has-due' : 'costrans-no-due'}`}>
                                                    Rs. {formatCurrency(selectedReference.balance || 0)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Expense Details - WITH CATEGORY DROPDOWN - FIXED expense name update */}
                            {isExpense && (
                                <div className="costrans-expense-section">
                                    <div className="costrans-expense-fields">
                                        <div className="costrans-field-group">
                                            <CustomExpenseDropdown
                                                value={formData.expense_category}
                                                onChange={(value) => {
                                                    // Find the category to get its label
                                                    const category = EXPENSE_CATEGORIES.find(cat => cat.value === value);
                                                    setFormData(prev => ({ 
                                                        ...prev, 
                                                        expense_category: value,
                                                        // Always update expense_name when category changes
                                                        expense_name: category ? category.label : ''
                                                    }));
                                                }}
                                                disabled={loading}
                                                placeholder={t('expenseCategory')}
                                                t={t}
                                                isRTL={isRTL}
                                            />
                                        </div>
                                        <div className="costrans-field-group">
                                            <input
                                                type="text"
                                                placeholder=" "
                                                value={formData.expense_name}
                                                onChange={e => setFormData(prev => ({ ...prev, expense_name: e.target.value }))}
                                                disabled={loading}
                                                className="costrans-input"
                                                style={{
                                                    borderColor: formData.expense_category ? 'var(--costrans-success)' : 'var(--costrans-border)'
                                                }}
                                            />
                                            <label>
                                                {t('expenseName')} 
                                                {!formData.expense_category && <span style={{ color: 'var(--costrans-error)' }}> *</span>}
                                                {formData.expense_category && <span className="costrans-optional" style={{ fontSize: '0.65rem', color: 'var(--costrans-text-light)' }}> (optional)</span>}
                                            </label>
                                            {formData.expense_category && formData.expense_name && (
                                                <span style={{
                                                    position: 'absolute',
                                                    right: isRTL ? 'auto' : '8px',
                                                    left: isRTL ? '8px' : 'auto',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    fontSize: '0.65rem',
                                                    color: 'var(--costrans-success)',
                                                    fontWeight: 500
                                                }}>
                                                    ✓ Auto-filled
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Payment Details - Only show for non-daily options */}
                            <div className="costrans-payment-section">
                                <div className="costrans-section-header">
                                    <Wallet size={16} />
                                    <span>{t('paymentDetails')}</span>
                                    {isAdvanceMode ? (
                                        <span className={`costrans-type-badge ${advanceConfig.direction === 'incoming' ? 'costrans-incoming-badge' : 'costrans-outgoing-badge'}`}>
                                            {advanceConfig.label.toUpperCase()}
                                        </span>
                                    ) : (
                                        <>
                                            {isCredit && <span className="costrans-type-badge costrans-incoming-badge">INCOMING</span>}
                                            {isDebit && <span className="costrans-type-badge costrans-outgoing-badge">OUTGOING</span>}
                                            {isOutgoing && <span className="costrans-type-badge costrans-outgoing-badge">OUTGOING</span>}
                                        </>
                                    )}
                                </div>

                                {isAdvanceEligible && (
                                    <div className="costrans-payment-mode" role="group" aria-label={t('paymentEntryType')}>
                                        <button
                                            type="button"
                                            className={`costrans-payment-mode-option ${!isAdvanceMode ? 'active' : ''}`}
                                            onClick={() => setPaymentEntryMode('regular')}
                                        >
                                            <ArrowUpCircle size={17} />
                                            <span>
                                                <strong>{`Regular ${currentOption?.label || t('regularPayment')}`}</strong>
                                                <small>{currentOption?.description || t('regularPaymentHint')}</small>
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`costrans-payment-mode-option advance ${isAdvanceMode ? 'active' : ''}`}
                                            onClick={() => {
                                                setPaymentEntryMode('advance');
                                                setSelectedInvoices([]);
                                                setIsFullPayment(false);
                                            }}
                                        >
                                            {advanceConfig.direction === 'incoming' ? <ArrowDownCircle size={17} /> : <ArrowUpCircle size={17} />}
                                            <span><strong>{advanceConfig.label}</strong><small>{advanceConfig.hint}</small></span>
                                        </button>
                                    </div>
                                )}

                                {isAdvanceMode && (
                                    <div className="costrans-advance-notice">
                                        <Info size={16} />
                                        <span>{advanceConfig.notice}</span>
                                    </div>
                                )}

                                <div className="costrans-payment-grid">
                                    <div className="costrans-field-group costrans-amount-field">
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder=" "
                                            className="costrans-amount-input"
                                            value={paymentAmount || formData.amount}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setPaymentAmount(val);
                                                setFormData(prev => ({ ...prev, amount: val }));
                                                handlePaymentAmountChange(val);
                                            }}
                                            disabled={loading || (!isExpense && !selectedReference)}
                                            style={{
                                                borderColor: isAdvanceMode
                                                    ? (advanceConfig.direction === 'incoming' ? 'var(--costrans-success)' : 'var(--costrans-error)')
                                                    : isCredit
                                                        ? 'var(--costrans-success)'
                                                        : isDebit
                                                            ? 'var(--costrans-error)'
                                                            : 'var(--costrans-border)'
                                            }}
                                        />
                                        <label>{t('amount')}</label>
                                    </div>

                                    <div className="costrans-field-group">
                                        <input
                                            type="date"
                                            placeholder=" "
                                            value={formData.transaction_date}
                                            onChange={e => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                                            disabled={loading}
                                        />
                                        <label><CalendarDays size={14} />{t('date')}</label>
                                    </div>

                                    <div className="costrans-field-group costrans-select-group">
                                        <select
                                            value={formData.payment_method}
                                            onChange={e => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                                            disabled={loading}
                                            className="costrans-select"
                                        >
                                            {paymentMethodOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <label><CreditCard size={14} />{t('paymentMethod')}</label>
                                        <ChevronDown size={12} className="costrans-select-arrow" />
                                    </div>

                                    <div className="costrans-field-group">
                                        <input
                                            type="text"
                                            placeholder=" "
                                            value={formData.invoice_number}
                                            onChange={e => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                            disabled={loading}
                                        />
                                        <label><FileText size={14} />{t('invoiceNumber')} <span className="costrans-optional">({t('optional')})</span></label>
                                    </div>
                                </div>

                                {/* Payment Preview */}
                                {!isExpense && selectedInvoices.length > 0 && (
                                    <div className="costrans-payment-preview">
                                        <div className="costrans-preview-row">
                                            <span>{t('totalDue')}:</span>
                                            <span>Rs. {formatCurrency(selectedTotal)}</span>
                                        </div>
                                        <div className="costrans-preview-row costrans-highlight">
                                            <span>{t('paymentAmount')}:</span>
                                            <span className="costrans-payment-amount">Rs. {formatCurrency(parseFloat(paymentAmount || formData.amount) || 0)}</span>
                                        </div>
                                        <div className="costrans-preview-row">
                                            <span>{t('paymentType')}:</span>
                                            <span className={isFullPayment ? 'costrans-full' : 'costrans-partial'}>
                                                {isFullPayment ? t('fullPayment') : t('partialPayment')}
                                            </span>
                                        </div>
                                        <div className="costrans-preview-row">
                                            <span>{t('remainingBalance')}:</span>
                                            <span className={remainingBalance > 0.01 ? 'costrans-due-amount' : 'costrans-no-due'}>
                                                Rs. {formatCurrency(Math.max(0, remainingBalance))}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Notes */}
                                <div className="costrans-field-group costrans-textarea-group">
                                    <textarea
                                        rows="2"
                                        placeholder=" "
                                        value={formData.description}
                                        onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        disabled={loading}
                                    />
                                    <label><StickyNote size={14} />{t('notes')}</label>
                                </div>

                                {/* Action Buttons */}
                                <div className="costrans-form-actions">
                                    <button
                                        type="button"
                                        className="costrans-btn costrans-btn-secondary"
                                        onClick={resetForm}
                                        disabled={loading}
                                    >
                                        <X size={16} />
                                        <span>{t('clear')}</span>
                                    </button>

                                    <button
                                        type="submit"
                                        className={`costrans-btn costrans-btn-primary ${isCredit ? 'costrans-incoming-btn' : ''} ${isDebit ? 'costrans-outgoing-btn' : ''}`}
                                        onClick={handleAddTransaction}
                                        disabled={loading || !currentShop || (!isExpense && !selectedReference)}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={16} className="costrans-spin" />
                                                <span>{t('saving')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} />
                                                <span>{t('save')}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddTransaction;
